import { styled } from "@linaria/react";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import { FileIcon, GlobeIcon } from "@phosphor-icons/react";

import FlexItem from "@gx/dls/containers/FlexItem";
import FlexWrapper, {
  AlignItems,
  FlexDirection,
  JustifyContent,
} from "@gx/dls/containers/FlexWrapper";
import HorizontalDivider from "@gx/dls/dividers/HorizontalDivider";
import Icon, { IconVariant } from "@gx/dls/icons/Icon";
import Text, { TextSize, TextVariant } from "@gx/dls/text/Text";
import { withTheme, type PropsWithTheme } from "@gx/dls/theme";

import FileDownload from "@/pages/sources/components/edit/tabs/content/FileDownload";

const CardWrapper = withTheme(styled.div<PropsWithTheme>`
  background-color: ${({ theme }) => theme.color.background.tertiary};
  border: 0.5px solid ${({ theme }) => theme.color.border.primary};
  border-radius: 6px;
  overflow: hidden;
  overscroll-behavior: auto;
  width: 100%;
  flex-shrink: 0;
`);

const CardImageHeaderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100px;
  flex-shrink: 0;
  overflow: hidden;
  overscroll-behavior: auto;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
`;

interface SourceContentFileBadgeProps {
  imgUrl?: string | null;
  header: string;
  subHeader?: string | null;
  icon?: PhosphorIcon;
  onClick?: () => void;
  isDownloadable?: boolean;
  objectStorageUri?: string;
  filename?: string;
}

const SourceContentFileBadge = ({
  imgUrl,
  header,
  subHeader,
  icon = FileIcon,
  onClick,
  isDownloadable,
  objectStorageUri,
  filename,
}: SourceContentFileBadgeProps) => {
  const showHeader = imgUrl !== undefined;

  return (
    <CardWrapper onClick={onClick}>
      {showHeader && (
        <>
          <CardImageHeaderWrapper>
            {imgUrl === null ? (
              <FlexWrapper
                alignItems={AlignItems.CENTER}
                justifyContent={JustifyContent.CENTER}
                fillWidth
                fillHeight
              >
                <Icon component={GlobeIcon} variant={IconVariant.DISABLED} size={24} />
              </FlexWrapper>
            ) : (
              <CardImage src={imgUrl} alt="" />
            )}
          </CardImageHeaderWrapper>
          <HorizontalDivider />
        </>
      )}
      <FlexWrapper alignItems={AlignItems.CENTER} gap={6} padding="8px 10px" overflow="hidden">
        <FlexItem shrink={0} grow={0}>
          <Icon component={icon} variant={IconVariant.TERTIARY} size={14} />
        </FlexItem>
        <FlexItem grow={1}>
          <FlexWrapper direction={FlexDirection.COLUMN} gap={1} overflow="hidden">
            <Text size={TextSize.BODY_SM} isEllipsis>
              {header}
            </Text>
            {subHeader && (
              <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY} isEllipsis>
                {subHeader}
              </Text>
            )}
          </FlexWrapper>
        </FlexItem>
        {isDownloadable && objectStorageUri && filename && (
          <FlexItem shrink={0} grow={0}>
            <FileDownload objectStorageUri={objectStorageUri} filename={filename} />
          </FlexItem>
        )}
      </FlexWrapper>
    </CardWrapper>
  );
};

export default SourceContentFileBadge;
